/**
 * Cart Utilities - MakeMyMask
 * Gestion COMPLÈTE du panier (theme.js désactivé)
 * Inspiré de l'architecture Dawn theme
 */

class CartUtils {
  static SELECTORS = {
    drawer: '#sideCartOverlay',
    drawerContent: '#sideCartContent',
    drawerInner: '.side-cart',
    cartIcon: '.icon-cart',
    cartCounter: '.icon-cart__counter',
    cartLink: 'a[href="/cart"]',
    productsContainer: '#productsInCart',
    quantitySelect: '.js-input-product-quantity',
    removeButton: '.js-product-remove-button',
    closeButton: '#sideCartCloseButton',
    checkoutButton: '#sideCartButton',
    addToCartForm: 'form[action*="/cart/add"]',
    productCardAddBtn: '.productcard--add-cart'
  };

  static EVENTS = {
    updated: 'cart:updated',
    refresh: 'cart:refresh',
    itemAdded: 'cart:item-added',
    itemRemoved: 'cart:item-removed',
    empty: 'cart:empty',
    drawerOpened: 'cart:drawer-opened',
    drawerClosed: 'cart:drawer-closed'
  };

  /**
   * Ajoute un produit au panier (implémentation interne)
   */
  static async _addToCart(variantId, quantity = 1, properties = {}) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      
      Object.keys(properties).forEach(key => {
        formData.append(`properties[${key}]`, properties[key]);
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Erreur ajout panier');
      }

      const data = await response.json();
      
      // Récupérer le panier complet pour avoir le bon item_count
      let cart = null;
      try {
        cart = await this.getCart();
      } catch (e) {
        console.warn('Impossible de récupérer le panier, utilisation des données de réponse:', e);
        // Fallback : essayer d'utiliser item_count de la réponse si disponible
        if (data.item_count !== undefined) {
          cart = { item_count: data.item_count };
        }
      }
      
      // Mettre à jour le drawer et l'ouvrir si activé dans les settings
      const enableDrawer = window.themeSettings?.enableNativeCartDrawer !== false;
      if (enableDrawer) {
        await this.refreshDrawer();
        this.openDrawer();
      } else {
        // Debug: vérifier que le paramètre est bien lu
        console.log('Cart drawer natif désactivé - paramètre:', window.themeSettings?.enableNativeCartDrawer);
      }
      
      // Mettre à jour le compteur avec le bon nombre
      if (cart && cart.item_count !== undefined) {
        this.updateCounter(cart.item_count);
      } else if (data.item_count !== undefined) {
        this.updateCounter(data.item_count);
      }
      
      // Émettre l'événement avec les données complètes du panier
      this.emit(this.EVENTS.itemAdded, { item: data });
      if (cart) {
        this.emit(this.EVENTS.updated, { cart: cart });
      } else {
        this.emit(this.EVENTS.updated, { cart: data });
      }

      return cart || data;
    } catch (error) {
      console.error('CartUtils.addToCart:', error);
      throw error;
    }
  }

  /**
   * Met à jour la quantité d'un item
   */
  static async updateQuantity(line, quantity) {
    try {
      // Trouver le produit correspondant et afficher le loader
      const productContainer = document.querySelector(`[data-line-index="${line}"]`)?.closest('.cart-product');
      const priceElement = productContainer?.querySelector('.cart-product__price');
      
      // Afficher le loader si on a trouvé l'élément prix
      if (priceElement) {
        priceElement.classList.add('is-loading');
        const loader = document.createElement('span');
        loader.className = 'cart-product__price-loader';
        loader.innerHTML = '<span class="cart-product__price-spinner"></span>';
        priceElement.appendChild(loader);
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });

      if (!response.ok) throw new Error('Erreur mise à jour');

      const cart = await response.json();
      
      await this.refreshDrawer();
      this.updateCounter(cart.item_count);
      
      if (quantity === 0) {
        this.emit(this.EVENTS.itemRemoved, { line });
      }
      if (cart.item_count === 0) {
        this.emit(this.EVENTS.empty);
      }
      this.emit(this.EVENTS.updated, { cart, source: 'side-cart', fromSideCart: true });

      return cart;
    } catch (error) {
      console.error('CartUtils.updateQuantity:', error);
      
      // En cas d'erreur, retirer le loader
      const productContainer = document.querySelector(`[data-line-index="${line}"]`)?.closest('.cart-product');
      const priceElement = productContainer?.querySelector('.cart-product__price');
      if (priceElement) {
        priceElement.classList.remove('is-loading');
        const loader = priceElement.querySelector('.cart-product__price-loader');
        if (loader) loader.remove();
      }
      
      throw error;
    }
  }

  /**
   * Supprime un item du panier
   */
  static async removeItem(line) {
    return this.updateQuantity(line, 0);
  }

  /**
   * Récupère le panier actuel
   */
  static async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Erreur récupération panier');
      return await response.json();
    } catch (error) {
      console.error('CartUtils.getCart:', error);
      throw error;
    }
  }

  /**
   * Rafraîchit le contenu du drawer
   */
  static async refreshDrawer() {
    // Ne pas rafraîchir si le drawer natif est désactivé
    const enableDrawer = window.themeSettings?.enableNativeCartDrawer !== false;
    if (!enableDrawer) {
      return;
    }

    const drawerBefore = document.querySelector(this.SELECTORS.drawer);
    const wasOpen = drawerBefore?.classList.contains('is-active');
    
    try {
      const response = await fetch('/?section_id=side-cart');
      if (!response.ok) return;
      
      const html = await response.text();
      // Utiliser le drawer directement au lieu de drawerContent qui n'existe pas
      const drawer = document.querySelector(this.SELECTORS.drawer);
      
      if (drawer) {
        // Créer un conteneur temporaire pour parser le HTML
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const newDrawer = temp.querySelector(this.SELECTORS.drawer);
        
        if (newDrawer) {
          // Remplacer le contenu du drawer
          drawer.innerHTML = newDrawer.innerHTML;
          
          // Réappliquer l'état ouvert si nécessaire
          if (wasOpen) {
            drawer.classList.add('is-active');
            document.body.style.overflow = 'hidden';
          }
          
          // Émettre l'événement refresh après un court délai pour laisser le DOM se stabiliser
          setTimeout(() => {
            this.emit(this.EVENTS.refresh);
            if (typeof window.hideB2BCartElements === 'function') {
              window.hideB2BCartElements();
            }
            if (typeof window.handleCartUpsellUpdate === 'function') {
              window.handleCartUpsellUpdate();
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error('CartUtils.refreshDrawer:', error);
    }
  }

  /**
   * Ouvre le drawer
   */
  static openDrawer() {
    // Vérifier si le drawer natif est activé dans les settings
    const enableDrawer = window.themeSettings?.enableNativeCartDrawer !== false;
    if (!enableDrawer) {
      return;
    }

    const drawer = document.querySelector(this.SELECTORS.drawer);
    if (drawer) {
      drawer.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      this.emit(this.EVENTS.drawerOpened);
    }
  }

  /**
   * Ferme le drawer
   */
  static closeDrawer() {
    const drawer = document.querySelector(this.SELECTORS.drawer);
    if (drawer) {
      drawer.classList.remove('is-active');
      document.body.style.overflow = '';
      this.emit(this.EVENTS.drawerClosed);
    }
  }

  /**
   * Met à jour le compteur du panier
   */
  static updateCounter(count) {
    // Vérifier que count est un nombre valide
    if (count === undefined || count === null || isNaN(count)) {
      console.warn('CartUtils.updateCounter: count invalide, récupération du panier...');
      // Récupérer le panier pour obtenir le bon count
      this.getCart().then(cart => {
        if (cart && cart.item_count !== undefined) {
          this.updateCounter(cart.item_count);
        }
      }).catch(e => {
        console.error('Erreur récupération panier pour compteur:', e);
      });
      return;
    }
    
    const counter = document.querySelector(this.SELECTORS.cartCounter);
    if (counter) {
      counter.textContent = `(${count})`;
    } else if (count > 0) {
      const iconCart = document.querySelector(this.SELECTORS.cartIcon);
      if (iconCart) {
        const newCounter = document.createElement('span');
        newCounter.className = 'icon-cart__counter';
        newCounter.textContent = `(${count})`;
        iconCart.appendChild(newCounter);
      }
    } else if (count === 0) {
      // Supprimer le compteur si le panier est vide
      const counter = document.querySelector(this.SELECTORS.cartCounter);
      if (counter) {
        counter.remove();
      }
    }
  }

  /**
   * Émet un événement personnalisé
   */
  static emit(eventName, detail = {}) {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  /**
   * Redirige vers le checkout
   */
  static goToCheckout() {
    window.location.href = '/checkout';
  }

  /**
   * Lie tous les événements (event delegation)
   */
  static bindAllEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;

    // === CLICS ===
    document.addEventListener('click', (e) => {
      // Icône panier - ouvrir le drawer
      if (e.target.closest(this.SELECTORS.cartIcon)) {
        e.preventDefault();
        this.openDrawer();
        return;
      }

      // Lien /cart - ouvrir le drawer
      if (e.target.closest(this.SELECTORS.cartLink)) {
        e.preventDefault();
        e.stopPropagation();
        this.openDrawer();
        return;
      }

      // Overlay - fermer le drawer
      const overlay = e.target.closest(this.SELECTORS.drawer);
      if (overlay && (e.target === overlay || e.target.classList.contains('js-close-side-cart'))) {
        this.closeDrawer();
        return;
      }

      // Bouton fermer
      if (e.target.closest(this.SELECTORS.closeButton)) {
        this.closeDrawer();
        return;
      }

      // Bouton checkout
      if (e.target.closest(this.SELECTORS.checkoutButton)) {
        this.goToCheckout();
        return;
      }

      // Bouton suppression
      const removeBtn = e.target.closest(this.SELECTORS.removeButton);
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const lineIndex = parseInt(removeBtn.dataset.lineIndex || removeBtn.closest('[data-line-index]')?.dataset.lineIndex);
        if (lineIndex) {
          // Trouver l'élément produit à supprimer
          const productElement = removeBtn.closest('.cart-product');
          
          // Animer la suppression avec slide out
          if (productElement && window.CartAnimations) {
            window.CartAnimations.animateItemRemoval(productElement).then(() => {
              // Supprimer après l'animation
              this.updateQuantity(lineIndex, 0);
            });
          } else {
            // Fallback si l'animation n'est pas disponible
            this.updateQuantity(lineIndex, 0);
          }
        }
        return;
      }

      // Bouton add-to-cart sur product cards (hors formulaire)
      const addBtn = e.target.closest(this.SELECTORS.productCardAddBtn);
      if (addBtn && !addBtn.closest('form')) {
        e.preventDefault();
        const variantId = addBtn.dataset.variantId || addBtn.value;
        if (variantId) {
          this.addToCart(variantId, 1);
        }
        return;
      }

      // Empêcher la propagation dans le drawer
      const drawerInner = e.target.closest(this.SELECTORS.drawerInner);
      if (drawerInner) {
        e.stopPropagation();
      }
    }, true);

    // === CHANGEMENTS ===
    document.addEventListener('change', (e) => {
      const select = e.target.closest(this.SELECTORS.quantitySelect);
      if (select) {
        e.stopPropagation();
        e.preventDefault();
        
        const container = select.closest('[data-line-index]');
        const lineIndex = parseInt(container?.dataset.lineIndex);
        const quantity = parseInt(select.value);
        
        if (lineIndex && !isNaN(quantity)) {
          this.updateQuantity(lineIndex, quantity);
        }
      }
    }, true);

    // === SOUMISSION FORMULAIRES ===
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest(this.SELECTORS.addToCartForm);
      if (!form || form.dataset.cartUtilsHandled) return;
      
      e.preventDefault();
      form.dataset.cartUtilsHandled = 'true';

      const button = form.querySelector('button[type="submit"], input[type="submit"]');
      
      // Sauvegarder le HTML complet du bouton pour préserver la structure
      let originalHTML = null;
      let originalValue = null;
      let loadingSpan = null;
      
      // État loading - préserver la structure HTML
      if (button) {
        if (button.tagName === 'BUTTON') {
          // Sauvegarder le HTML complet
          originalHTML = button.innerHTML;
          
          // Trouver le span visible (celui qui n'a pas la classe hidden) pour le modifier sans casser la structure
          // Priorité : d'abord chercher par ID, puis par classe parmi les spans visibles
          loadingSpan = button.querySelector('#add-to-cart__information-add:not(.hidden)');
          
          // Si pas trouvé par ID, chercher le premier span avec la classe qui n'est pas caché
          if (!loadingSpan) {
            const allSpans = button.querySelectorAll('.add-to-cart__information');
            for (let span of allSpans) {
              if (!span.classList.contains('hidden')) {
                loadingSpan = span;
                break;
              }
            }
          }
          
          if (loadingSpan) {
            // Sauvegarder le texte original du span
            loadingSpan.dataset.originalText = loadingSpan.textContent.trim();
            loadingSpan.textContent = 'Ajout...';
          } else {
            // Fallback si on ne trouve pas le span : sauvegarder et modifier temporairement
            button.innerHTML = '<span class="add-to-cart__information">Ajout...</span>';
          }
        } else {
          // Pour les inputs, on garde l'ancien comportement
          originalValue = button.value || '';
          button.value = 'Ajout...';
        }
        
        button.disabled = true;
      }

      try {
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        
        // Extraire toutes les propriétés du formulaire
        const properties = {};
        for (const [key, value] of formData.entries()) {
          // Les propriétés sont au format "properties[nom]"
          if (key.startsWith('properties[') && key.endsWith(']')) {
            const propertyName = key.slice(11, -1); // Enlever "properties[" et "]"
            properties[propertyName] = value;
          }
        }
        
        // Détecter si c'est un produit ajouté via upsell
        if (form.dataset.upsell === 'true') {
          // Tag simple pour identifier les produits ajoutés via les recommandations du panier
          properties['_from_cart_upsell'] = 'true';
        }
        
        await this.addToCart(variantId, quantity, properties);
      } catch (error) {
        console.error('Erreur ajout panier:', error);
      } finally {
        if (button) {
          button.disabled = false;
          
          if (button.tagName === 'BUTTON') {
            // Restaurer le HTML original
            if (loadingSpan && loadingSpan.dataset.originalText) {
              // Restaurer le texte du span
              loadingSpan.textContent = loadingSpan.dataset.originalText;
              delete loadingSpan.dataset.originalText;
            } else if (originalHTML) {
              // Restaurer tout le HTML si on avait fait un fallback
              button.innerHTML = originalHTML;
            }
          } else {
            // Pour les inputs
            if (originalValue !== null) {
              button.value = originalValue;
            }
          }
        }
        delete form.dataset.cartUtilsHandled;
      }
    }, true);

    // === CLAVIER ===
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') {
        this.closeDrawer();
      }
    });
  }

  /**
   * Initialise CartUtils
   */
  static init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._init());
    } else {
      this._init();
    }
  }

  static _init() {
    this.bindAllEvents();
    this.patchFetchForThirdParty();
  }

  /**
   * Intercepte les appels fetch tiers à /cart/add.js (PageFly, etc.)
   * pour rafraîchir et ouvrir le side cart automatiquement.
   */
  static patchFetchForThirdParty() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const method = (args[1]?.method || 'GET').toUpperCase();
      const isCartAdd = method === 'POST' && url.includes('/cart/add');

      // Si c'est CartUtils qui appelle, ne pas intercepter
      if (isCartAdd && self._internalFetch) {
        return originalFetch.apply(this, args);
      }

      const result = originalFetch.apply(this, args);

      if (isCartAdd) {
        result.then(response => {
          if (response.ok) {
            // Cloner pour ne pas consommer le body
            response.clone().json().then(() => {
              self.refreshDrawer().then(() => self.openDrawer());
              self.getCart().then(cart => self.updateCounter(cart.item_count)).catch(() => {});
            }).catch(() => {});
          }
        }).catch(() => {});
      }

      return result;
    };
  }

  /**
   * Ajoute un produit au panier (version interne avec flag)
   */
  static async addToCart(variantId, quantity = 1, properties = {}) {
    this._internalFetch = true;
    try {
      return await this._addToCart(variantId, quantity, properties);
    } finally {
      this._internalFetch = false;
    }
  }
}

// Rendre disponible globalement
window.CartUtils = CartUtils;

// Auto-init
CartUtils.init();
